// Vite entry. Capture the one-time device credential, initialize i18n with the
// device-default locale, and mount React IMMEDIATELY — config resolution runs
// inside the App's loading gate so the boot window is announced instead of a
// blank #root (SEM-4922). No OIDC: this shared terminal authenticates with
// ADR-020 KioskDevice credentials + per-employee PIN.

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import "@/styles/globals.css";
import { App } from "@/App";
import { captureProvisionedDeviceToken } from "@/api/device-token";
import { resolveKioskBootstrap, type KioskBootstrapState } from "@/bootstrap";
import { initI18n, KIOSK_DEFAULT_LOCALE } from "@/i18n";

async function bootstrap(): Promise<void> {
  captureProvisionedDeviceToken();
  // Bundled resources only — this await never touches the network, so the
  // first paint is not gated on anything slower than module evaluation.
  const i18n = await initI18n(KIOSK_DEFAULT_LOCALE);

  // Sync <html lang> + document.title with the resolved locale so screen
  // readers pronounce content correctly (WCAG 2.2 — 3.1.1 Language of Page).
  const syncDocumentLocale = (lng: string): void => {
    document.documentElement.lang = lng;
    document.title = i18n.t("app.title");
  };
  syncDocumentLocale(i18n.language);
  i18n.on("languageChanged", syncDocumentLocale);

  // One function serves both the initial load and the unavailable-gate retry:
  // fetch live config, then follow its locale. The App keeps terminal
  // provisioning and feature-disable states fail-closed regardless.
  const resolveBootstrap = async (): Promise<KioskBootstrapState> => {
    const nextConfig = await resolveKioskBootstrap();
    if (i18n.language !== nextConfig.locale) {
      await i18n.changeLanguage(nextConfig.locale);
    }
    return nextConfig;
  };

  const container = document.getElementById("root");
  if (!container) {
    throw new Error("kiosk: #root element missing from index.html");
  }

  createRoot(container).render(
    <StrictMode>
      <App resolveBootstrap={resolveBootstrap} retryBootstrap={resolveBootstrap} />
    </StrictMode>,
  );
}

void bootstrap();
